import { useCallback, useEffect, useRef, useState } from "react";
import { Undo2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui";

// =============================================================================
// VehicleDamageSketch — circle the damage on an outline of the vehicle
// (mig 482 piece 2b, 1 Nov 2026)
// =============================================================================
// James, 1 Nov 2026, on the free-text "Hilux / Motorcycle" boxes: "I want a
// Hilux sketch black and white and a circle the damaged area." So: a black-
// and-white outline of the vehicle — ute, car, van/truck or motorbike, all
// drawn here, no image files — seen from above with the nose at the top, and
// the driver circles the damage with a finger. Up to a few strokes, Undo,
// Clear. Exports a PNG (exportBlob on the handle) that the long form uploads
// to hse-evidence like a photo; the broker report prints it next to the
// damage description.
// =============================================================================

export type VehicleShape = "ute" | "car" | "van" | "bike";
export const VEHICLE_SHAPES: Array<[VehicleShape, string]> = [["ute", "Ute"], ["car", "Car / SUV"], ["van", "Van / truck"], ["bike", "Motorbike"]];
export const DW = 700, DH = 520;

export interface VehicleDamageHandle { exportBlob: () => Promise<Blob | null>; isEmpty: () => boolean }

export function VehicleDamageSketch({ shape, strokes, onChange, title, handleRef }: {
  shape: VehicleShape;
  strokes: Array<Array<[number, number]>>;
  onChange: (s: Array<Array<[number, number]>>) => void;
  title: string;
  handleRef: React.MutableRefObject<VehicleDamageHandle | null>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [draft, setDraft] = useState<Array<[number, number]> | null>(null);

  const paint = useCallback((g: CanvasRenderingContext2D, forExport = false) => {
    g.clearRect(0, 0, DW, DH); g.fillStyle = "#fff"; g.fillRect(0, 0, DW, DH);
    drawOutline(g, shape);
    if (forExport) { g.fillStyle = "#333"; g.font = "600 16px sans-serif"; g.textAlign = "left"; g.textBaseline = "bottom"; g.fillText(title, 16, DH - 10); }
    const all = draft ? [...strokes, draft] : strokes;
    for (const s of all) {
      g.save(); g.strokeStyle = "#e0323a"; g.lineWidth = 6; g.lineCap = "round"; g.lineJoin = "round";
      g.beginPath(); s.forEach(([x, y], i) => i ? g.lineTo(x, y) : g.moveTo(x, y)); g.stroke(); g.restore();
    }
  }, [shape, strokes, draft, title]);

  useEffect(() => { const cv = canvasRef.current; if (cv) paint(cv.getContext("2d")!); }, [paint]);
  useEffect(() => {
    handleRef.current = {
      isEmpty: () => strokes.length === 0,
      exportBlob: async () => { const cv = document.createElement("canvas"); cv.width = DW; cv.height = DH; paint(cv.getContext("2d")!, true); return new Promise(res => cv.toBlob(b => res(b), "image/png")); },
    };
  }, [paint, strokes.length, handleRef]);

  const toLogical = (e: React.PointerEvent) => { const r = canvasRef.current!.getBoundingClientRect(); return [((e.clientX - r.left) / r.width) * DW, ((e.clientY - r.top) / r.height) * DH] as [number, number]; };

  return (
    <div className="space-y-1.5">
      <canvas ref={canvasRef} width={DW} height={DH}
        onPointerDown={e => { e.preventDefault(); (e.target as HTMLElement).setPointerCapture(e.pointerId); setDraft([toLogical(e)]); }}
        onPointerMove={e => { if (!draft) return; e.preventDefault(); setDraft(d => d ? [...d, toLogical(e)] : d); }}
        onPointerUp={() => { if (draft && draft.length > 2) onChange([...strokes, draft]); setDraft(null); }}
        onPointerCancel={() => setDraft(null)}
        className="w-full rounded-md border border-line bg-white select-none" style={{ touchAction: "none", aspectRatio: `${DW} / ${DH}` }}/>
      <div className="flex items-center gap-1.5">
        <Button size="sm" variant="secondary" icon={Undo2} disabled={strokes.length === 0} onClick={() => onChange(strokes.slice(0, -1))}>Undo</Button>
        <Button size="sm" variant="ghost" icon={Trash2} disabled={strokes.length === 0} onClick={() => onChange([])}>Clear</Button>
        <span className="text-[11px] text-text-muted ml-auto">Circle the damage with your finger</span>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// the outlines — top view, nose up, black on white
// -----------------------------------------------------------------------------
function rr(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  g.beginPath(); g.moveTo(x + r, y); g.lineTo(x + w - r, y); g.quadraticCurveTo(x + w, y, x + w, y + r);
  g.lineTo(x + w, y + h - r); g.quadraticCurveTo(x + w, y + h, x + w - r, y + h); g.lineTo(x + r, y + h);
  g.quadraticCurveTo(x, y + h, x, y + h - r); g.lineTo(x, y + r); g.quadraticCurveTo(x, y, x + r, y); g.closePath();
}
function label(g: CanvasRenderingContext2D, t: string, x: number, y: number) { g.fillStyle = "#777"; g.font = "13px sans-serif"; g.textAlign = "center"; g.textBaseline = "middle"; g.fillText(t, x, y); }
function wheels(g: CanvasRenderingContext2D, cx: number, halfW: number, ys: number[]) {
  g.fillStyle = "#222";
  for (const y of ys) { rr(g, cx - halfW - 10, y - 22, 14, 44, 5); g.fill(); rr(g, cx + halfW - 4, y - 22, 14, 44, 5); g.fill(); }
}

export function drawOutline(g: CanvasRenderingContext2D, shape: VehicleShape) {
  g.save(); g.strokeStyle = "#111"; g.lineWidth = 3; g.lineJoin = "round";
  const cx = 350;
  if (shape === "bike") {
    // narrow: front wheel, forks/bars, tank, seat, rear wheel
    g.fillStyle = "#222"; rr(g, cx - 14, 60, 28, 80, 12); g.fill(); rr(g, cx - 14, 380, 28, 80, 12); g.fill();
    g.beginPath(); g.moveTo(cx - 70, 150); g.lineTo(cx + 70, 150); g.stroke();                 // handlebars
    g.fillStyle = "#fff"; rr(g, cx - 32, 165, 64, 110, 18); g.fill(); g.stroke();              // tank
    rr(g, cx - 26, 275, 52, 110, 14); g.fill(); g.stroke();                                    // seat
    g.beginPath(); g.moveTo(cx - 60, 230); g.lineTo(cx - 60, 320); g.moveTo(cx + 60, 230); g.lineTo(cx + 60, 320); g.stroke();  // footpegs/exhaust
    label(g, "FRONT", cx, 30); label(g, "REAR", cx, 490); label(g, "LEFT", 150, 260); label(g, "RIGHT", 550, 260);
  } else {
    const halfW = shape === "van" ? 95 : 85, top = 50, bot = 470;
    wheels(g, cx, halfW, shape === "van" ? [130, 400] : [130, 390]);
    g.fillStyle = "#fff"; rr(g, cx - halfW, top, halfW * 2, bot - top, shape === "van" ? 14 : 26); g.fill(); g.stroke();    // body
    // bonnet line, windscreen, roof/cab, rear glass
    const ws = top + 70;
    g.beginPath(); g.moveTo(cx - halfW + 8, ws); g.quadraticCurveTo(cx, ws - 20, cx + halfW - 8, ws); g.stroke();       // windscreen top
    g.beginPath(); g.moveTo(cx - halfW + 14, ws + 36); g.lineTo(cx + halfW - 14, ws + 36); g.stroke();                  // windscreen bottom
    if (shape === "ute") {
      rr(g, cx - halfW + 10, ws + 36, halfW * 2 - 20, 100, 8); g.stroke();                                              // cab roof
      g.beginPath(); g.moveTo(cx - halfW, ws + 150); g.lineTo(cx + halfW, ws + 150); g.stroke();                         // cab / tray split
      rr(g, cx - halfW + 12, ws + 165, halfW * 2 - 24, bot - ws - 185, 6); g.stroke();                                   // tray
      label(g, "TRAY", cx, ws + 260);
      // mirrors
    } else if (shape === "car") {
      rr(g, cx - halfW + 10, ws + 36, halfW * 2 - 20, 190, 10); g.stroke();                                             // roof
      g.beginPath(); g.moveTo(cx - halfW + 14, bot - 90); g.lineTo(cx + halfW - 14, bot - 90); g.stroke();              // rear glass
      g.beginPath(); g.moveTo(cx - halfW + 8, bot - 60); g.quadraticCurveTo(cx, bot - 40, cx + halfW - 8, bot - 60); g.stroke();
    } else {
      rr(g, cx - halfW + 10, ws + 36, halfW * 2 - 20, bot - ws - 60, 8); g.stroke();                                     // box
      g.beginPath(); g.moveTo(cx - halfW, ws + 110); g.lineTo(cx + halfW, ws + 110); g.stroke();                         // cab / box
    }
    // mirrors and lights
    g.beginPath(); g.moveTo(cx - halfW, ws + 40); g.lineTo(cx - halfW - 16, ws + 36); g.moveTo(cx + halfW, ws + 40); g.lineTo(cx + halfW + 16, ws + 36); g.stroke();
    rr(g, cx - halfW + 10, top + 6, 26, 10, 3); g.stroke(); rr(g, cx + halfW - 36, top + 6, 26, 10, 3); g.stroke();
    rr(g, cx - halfW + 10, bot - 16, 26, 10, 3); g.stroke(); rr(g, cx + halfW - 36, bot - 16, 26, 10, 3); g.stroke();
    label(g, "FRONT", cx, 26); label(g, "REAR", cx, 498); label(g, "LEFT (passenger)", 130, 260); label(g, "RIGHT (driver)", 570, 260);
  }
  g.restore();
}
